import { warn } from '@/lib/logger'
import isEqual from 'lodash/isEqual'

// Save layer order for future reference inserting layers
const layerOrder = {}

/**
 * Shows or hides style layers.  If layer isn't managed by mapbox yet, it will
 * be added.  If layers are already managed by mapbox, their visibility will be
 * toggled.
 * @param {any} map
 * @param {Object<string, any>} newLayers
 * @param {Object<string, any>} [oldLayers]
 */
export const shownLayers = (map, newLayers, oldLayers) => {
  oldLayers ??= {}

  const showStyleLayers = []
  const hideLayerIds = []

  // which layers should be shown
  for (const layerId of Object.keys(newLayers)) {
    if (layerId in oldLayers === false) {
      showStyleLayers.push(newLayers[layerId])
    } else {
      // Remove key from old layers if found.
      // This will keep hidden layers iteration limited to only those which
      // will be hidden.
      delete oldLayers[layerId]
    }
  }

  // which style layers should be hidden
  for (const layerId of Object.keys(oldLayers)) {
    if (layerId in newLayers === false) {
      hideLayerIds.push(layerId)
    }
  }

  showLayers(map, showStyleLayers)
  hideLayers(map, hideLayerIds)
}

/**
 * Shows all style layers passed.  If layers are already managed by mapbox,
 * their visibility will be toggled.
 * @param {any} map
 * @param {any[]} layers
 */
const showLayers = (map, layers) => {
  if (!layers.length) {
    return
  }

  for (const layer of layers) {
    const stylesheet = map.getStyle()
    const layerId = layer.id

    const mapLayer = map.getLayer(layerId)
    if (mapLayer) {
      // Layer is already present
      map.setLayoutProperty(layerId, 'visibility', 'visible')
      continue
    }

    // Comparing current map stylesheet to original stylesheet layer ordering
    // to find the beforeId for insertion into the mapbox managed map.
    //
    // Could be undefined, if no layer exists before in mapbox styled map yet.
    const beforeIndex = stylesheet.layers.findIndex(
      ({ id }) => layerOrder[id] > layerOrder[layerId]
    )
    const beforeId = stylesheet.layers[beforeIndex]?.id
    map.addLayer(layer, beforeId)
  }
}

/**
 * Hides all style layers passed.  Hidden layers are not removed from mapbox
 * managed map, just visibility set to none.
 * @param {any} map
 * @param {string[]} layerIds
 */
const hideLayers = (map, layerIds) => {
  for (const layerId of layerIds) {
    const mapLayer = map.getLayer(layerId)
    if (!mapLayer) {
      // Not throwing warning, since this is expected with the way basemaps
      // work today.
      continue
    }

    map.setLayoutProperty(layerId, 'visibility', 'none')
  }
}

/**
 * Sets style layer opacities.
 * @param {any} map
 * @param {Object<string, number>} newOpacities
 * @param {Object<string, number>} [oldOpacities]
 */
export const setLayerOpacities = (map, newOpacities, oldOpacities) => {
  oldOpacities ??= {}

  for (const [layerId, opacity] of Object.entries(newOpacities)) {
    const hadLayer = layerId in oldOpacities
    if (hadLayer && opacity === oldOpacities[layerId]) {
      continue
    }

    map.setPaintProperty(layerId, 'raster-opacity', opacity)
  }
}

/**
 * Sets style layer filters.
 * @param {any} map
 * @param {Object<string, any[]>} newFilters
 * @param {Object<string, any[]>} [oldFilters]
 */
export const setLayerFilters = (map, newFilters, oldFilters) => {
  oldFilters ??= {}

  for (const [layerId, filter] of Object.entries(newFilters)) {
    if (isEqual(oldFilters?.[layerId], filter)) {
      continue
    } else if (!map.getLayer(layerId)) {
      warn(
        'Map',
        `Unable to set layer filter because map layer ${layerId} does not exist.`
      )
      continue
    }

    map.setFilter(layerId, filter)
  }
}

/**
 * Sets new sources on map.  For now, we are simply overriding the existing
 * mapbox managed sources and calling `setStyle(...)`.  Performance may suffer
 * using this method if mapbox managed layers grows too large.
 * @param {any} map
 * @param {Object<string, any>} newSources
 */
export const setNewSources = (map, newSources) => {
  const sourceTypes = ['raster', 'vector']
  const currentStylesheet = map.getStyle()
  const currentSources = currentStylesheet.sources
  let updatedSourceCount = 0
  for (const key in currentSources) {
    // only update raster or vector sources
    if (!sourceTypes.includes(currentSources[key].type)) {
      continue
    }

    // compare sources for changes
    const sourceEqual = isEqual(currentSources[key], newSources[key])
    if (sourceEqual === false) {
      currentSources[key] = newSources[key]
      updatedSourceCount++
    }
  }
  if (updatedSourceCount > 0) {
    map.setStyle(currentStylesheet)
  }
}

/**
 * *(AI-generated)*
 * Records the original ordering of layers for use when inserting new layers.
 */
export const setLayerOrder = (layers) => {
  layers.forEach((layer, index) => {
    const { id } = layer
    layerOrder[id] = index
  })
}
